import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResourceDownloads = ({ currentLanguage, translations }) => {
  const [downloadingId, setDownloadingId] = useState(null);

  const resources = [
  {
    id: 1,
    title: translations?.resources?.items?.partnershipGuide?.title,
    description: translations?.resources?.items?.partnershipGuide?.description,
    type: "PDF",
    size: "2.4 MB",
    pages: "24 pages",
    icon: "FileText",
    category: "Partnership",
    downloadCount: "1,247",
    lastUpdated: "October 2024",
    languages: ["EN", "DE", "FR", "ES"],
    preview: "https://images.unsplash.com/photo-1620887135506-b4a29bcaed32",
    previewAlt: "Document preview showing partnership guidelines with charts and text on white background"
  },
  {
    id: 2,
    title: translations?.resources?.items?.investmentCriteria?.title,
    description: translations?.resources?.items?.investmentCriteria?.description,
    type: "PDF",
    size: "1.8 MB",
    pages: "16 pages",
    icon: "TrendingUp",
    category: "Investment",
    downloadCount: "892",
    lastUpdated: "September 2024",
    languages: ["EN", "DE"],
    preview: "https://images.unsplash.com/photo-1571677246347-5040036b95cc",
    previewAlt: "Investment criteria document with financial charts and graphs on laptop screen"
  },
  {
    id: 3,
    title: translations?.resources?.items?.applicationForm?.title,
    description: translations?.resources?.items?.applicationForm?.description,
    type: "PDF",
    size: "0.8 MB",
    pages: "8 pages",
    icon: "FileCheck",
    category: "Application",
    downloadCount: "2,156",
    lastUpdated: "October 2024",
    languages: ["EN", "DE", "FR", "ES"],
    preview: "https://images.unsplash.com/photo-1693045181229-3eddc1a2e242",
    previewAlt: "Application form template with fillable fields and checkboxes on white paper"
  },
  {
    id: 4,
    title: translations?.resources?.items?.marketReport?.title,
    description: translations?.resources?.items?.marketReport?.description,
    type: "PDF",
    size: "5.2 MB",
    pages: "48 pages",
    icon: "BarChart3",
    category: "Research",
    downloadCount: "634",
    lastUpdated: "October 2024",
    languages: ["EN", "DE"],
    preview: "https://images.unsplash.com/photo-1605512929840-c280c33760fe",
    previewAlt: "Market research report with colorful charts and data visualization on computer screen"
  },
  {
    id: 5,
    title: translations?.resources?.items?.dueDiligence?.title,
    description: translations?.resources?.items?.dueDiligence?.description,
    type: "PDF",
    size: "3.1 MB",
    pages: "32 pages",
    icon: "Search",
    category: "Process",
    downloadCount: "445",
    lastUpdated: "September 2024",
    languages: ["EN", "DE", "FR"],
    preview: "https://images.unsplash.com/photo-1622100223462-297e5aaeac9e",
    previewAlt: "Due diligence checklist document with magnifying glass and business papers"
  },
  {
    id: 6,
    title: translations?.resources?.items?.portfolioOverview?.title,
    description: translations?.resources?.items?.portfolioOverview?.description,
    type: "PDF",
    size: "4.7 MB",
    pages: "36 pages",
    icon: "Briefcase",
    category: "Portfolio",
    downloadCount: "1,089",
    lastUpdated: "October 2024",
    languages: ["EN", "DE", "FR", "ES"],
    preview: "https://images.unsplash.com/photo-1651129520737-7137123b7611",
    previewAlt: "Portfolio overview document showing company logos and performance metrics"
  }];


  const categories = [
  { id: 'all', label: translations?.resources?.categories?.all, count: resources?.length },
  { id: 'Partnership', label: translations?.resources?.categories?.partnership, count: resources?.filter((r) => r?.category === 'Partnership')?.length },
  { id: 'Investment', label: translations?.resources?.categories?.investment, count: resources?.filter((r) => r?.category === 'Investment')?.length },
  { id: 'Application', label: translations?.resources?.categories?.application, count: resources?.filter((r) => r?.category === 'Application')?.length },
  { id: 'Research', label: translations?.resources?.categories?.research, count: resources?.filter((r) => r?.category === 'Research')?.length }];


  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredResources = resources?.filter((resource) => {
    const matchesCategory = selectedCategory === 'all' || resource?.category === selectedCategory;
    const matchesSearch = resource?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    resource?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleDownload = async (resource) => {
    setDownloadingId(resource?.id);

    // Mock download delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Mock download action
    const link = document.createElement('a');
    link.href = '#';
    link.download = `${resource?.title?.replace(/\s+/g, '_')}.pdf`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    setDownloadingId(null);

    // Show success message
    alert(`${resource?.title} ${translations?.resources?.downloadSuccess}`);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {translations?.resources?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {translations?.resources?.subtitle}
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={translations?.resources?.search?.placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />

            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories?.map((category) =>
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category?.id ?
                'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'}`
                }>

                  {category?.label} ({category?.count})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredResources?.map((resource) =>
          <div
            key={resource?.id}
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">

              {/* Preview Image */}
              <div className="relative h-48 bg-gray-100 overflow-hidden">
                <img
                src={resource?.preview}
                alt={resource?.previewAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                <div className="absolute top-4 left-4">
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                    {resource?.type}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-full">
                    {resource?.size}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name={resource?.icon} size={20} className="text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">{resource?.category}</span>
                  </div>
                  <div className="flex space-x-1">
                    {resource?.languages?.map((lang) =>
                  <span
                    key={lang}
                    className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">

                        {lang}
                      </span>
                  )}
                  </div>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {resource?.title}
                </h3>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {resource?.description}
                </p>

                {/* Metadata */}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>{resource?.pages}</span>
                  <span>{resource?.downloadCount} {translations?.resources?.downloads}</span>
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  {translations?.resources?.lastUpdated}: {resource?.lastUpdated}
                </div>

                {/* Download Button */}
                <Button
                variant="default"
                fullWidth
                loading={downloadingId === resource?.id}
                disabled={downloadingId !== null}
                onClick={() => handleDownload(resource)}
                iconName="Download"
                iconPosition="left"
                className="bg-blue-600 hover:bg-blue-700">

                  {downloadingId === resource?.id ?
                translations?.resources?.downloading :
                translations?.resources?.download
                }
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default ResourceDownloads;